<template>
	<AppHeader />
	<div class="landing-bg">
		<div class="landing-content">
			<!-- Loading -->
			<div v-if="isLoading" class="loading-container">
				<LoaderSciFi />
			</div>

			<!-- Title with typing cursor + quote rotation -->
			<div v-else class="title-row">
				<WelcomeTitle :title="features.title || 'Welcome to the Core'" />
			</div>

			<!-- Holographic panels -->
			<div v-if="!isLoading" class="panels-row">
				<div
					v-for="(feature, index) in features.items"
					:key="index"
					class="holo-wrapper"
					:class="{
						'locked': feature.title === 'Nucleus' && !userStore.isLoggedIn,
						'clickable': !(feature.title === 'Nucleus' && !userStore.isLoggedIn)
					}"
					@click="handleFeatureClick(feature)"
				>
					<div class="holo-card" :class="{ 'holo-locked': feature.title === 'Nucleus' && !userStore.isLoggedIn }">
						<div class="holo-header">{{ feature.title }}</div>
						<div class="holo-body">{{ feature.description }}</div>
						<div class="holo-footer">{{ feature.subtext }}</div>

						<!-- Access Denied Overlay -->
						<div
							v-if="feature.title === 'Nucleus' && !userStore.isLoggedIn"
							class="access-denied-overlay"
						>
							<div class="access-denied-text">
								ACCESS DENIED<br/>
								LOGIN REQUIRED
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<DialogProfile :openDialog="userStore.openProfileDialog" />
	</div>
</template>

<script setup lang="ts">
	definePageMeta({
		colorMode: 'dark',
		layout: 'full',
	});

	import { useUserStore } from '../stores/user';
	import { getAuth, onAuthStateChanged } from 'firebase/auth';
	import { userInfo2UserState } from '../models/user';

	const userStore = useUserStore();
	const propertyStore = usePropertyStore();
	const itemsStore = useItemStore();

	const storesLoading = ref(true);

	onBeforeMount(async () => {
		const auth = getAuth();
		onAuthStateChanged(auth, async (user) => {
			if (user != null) {
				userStore.setUser(userInfo2UserState(user));
			}
		});
	});

	onMounted(async () => {
		try {
			await Promise.all([
				propertyStore.fetchAll(),
				itemsStore.fetchItems()
			]);
		} catch (error) {
			console.error('Index: Error loading stores:', error);
		} finally {
			storesLoading.value = false;
		}
	});

	const { data: pageContent, pending } = await useLazyAsyncData('index-features', () =>
		queryCollection('features').first()
	);

	const isLoading = computed(() => pending.value || storesLoading.value);
	const features = computed(() => pageContent.value?.features || {});

	const handleFeatureClick = async (feature: any) => {
		if (feature.title === 'Nucleus' && !userStore.isLoggedIn) return;
		if (feature.target === '_blank') {
			window.open(feature.to, '_blank');
		} else {
			await navigateTo(feature.to);
		}
	};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap');

.landing-bg {
	background: #030608;
	height: 94vh;
	overflow: hidden;
}

.landing-content {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	min-height: 92vh;
	padding: 2rem;
}

.title-row {
	position: absolute;
	top: 15vh;
	left: 0;
	right: 0;
	text-align: center;
}

.loading-container {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 300px;
	width: 100%;
}

.panels-row {
	position: absolute;
	bottom: 20vh;
	display: flex;
	gap: 2rem;
	justify-content: center;
	width: 80vw;
}

/* Holographic card wrapper — handles the border trace */
.holo-wrapper {
	position: relative;
	width: 24vw;
	border-radius: 4px;
}

.holo-wrapper.clickable {
	cursor: pointer;
}

.holo-wrapper.locked {
	cursor: not-allowed;
}

/* The rotating border trace — conic gradient behind the card */
.holo-wrapper::before {
	content: '';
	position: absolute;
	inset: -2px;
	border-radius: 4px;
	background: conic-gradient(from var(--angle, 0deg), transparent 60%, #00ffea 100%);
	opacity: 0;
	transition: opacity 0.4s;
	z-index: 0;
}

.holo-wrapper:hover::before {
	opacity: 1;
	animation: border-trace 3s linear infinite;
}

/* Locked: red trace instead */
.holo-wrapper.locked::before {
	background: conic-gradient(from var(--angle, 0deg), transparent 60%, #ff4444 100%);
}

.holo-wrapper.locked:hover::before {
	opacity: 1;
	animation: border-trace 3s linear infinite;
}

@property --angle {
	syntax: '<angle>';
	initial-value: 0deg;
	inherits: false;
}

@keyframes border-trace {
	to { --angle: 360deg; }
}

/* The card itself */
.holo-card {
	position: relative;
	z-index: 1;
	background: rgba(5, 10, 14, 0.9);
	border: 1px solid #00ffea25;
	border-radius: 4px;
	overflow: hidden;
	transition: border-color 0.3s, box-shadow 0.3s;
	display: flex;
	flex-direction: column;
	height: 100%;
}

.holo-wrapper:hover .holo-card {
	border-color: #00ffea50;
	box-shadow: 0 0 30px #00ffea10;
}

.holo-locked {
	border-color: #ff444425;
}

.holo-wrapper.locked:hover .holo-card {
	border-color: #ff444450;
	box-shadow: 0 0 30px #ff444410;
}

.holo-header {
	font-family: 'Share Tech Mono', 'Courier New', monospace;
	font-size: 1.4em;
	font-weight: bold;
	text-align: center;
	padding: 1.2rem 1rem;
	color: #ff6600;
	letter-spacing: 3px;
	text-transform: uppercase;
	background: rgba(10, 20, 24, 0.8);
	border-bottom: 1px solid #00ffea15;
}

.holo-body {
	font-family: 'Share Tech Mono', 'Courier New', monospace;
	font-size: 0.95em;
	padding: 1.5rem 1.2rem;
	color: #00ffea;
	text-align: center;
	letter-spacing: 1px;
	line-height: 1.6;
	flex: 1;
}

.holo-footer {
	font-family: 'Share Tech Mono', 'Courier New', monospace;
	font-size: 0.75em;
	padding: 0.8rem 1rem;
	color: #00ffea60;
	text-align: center;
	letter-spacing: 2px;
	text-transform: uppercase;
	background: rgba(10, 20, 24, 0.8);
	border-top: 1px solid #00ffea10;
}

/* Access Denied Overlay */
.access-denied-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.92);
	display: flex;
	justify-content: center;
	align-items: center;
	opacity: 0;
	transition: opacity 0.3s;
	pointer-events: none;
	z-index: 10;
}

.holo-wrapper.locked:hover .access-denied-overlay {
	opacity: 1;
}

.access-denied-text {
	font-family: 'Share Tech Mono', 'Courier New', monospace;
	color: #ff4444;
	font-size: 1.1em;
	font-weight: bold;
	text-align: center;
	letter-spacing: 3px;
	line-height: 2;
	animation: denied-pulse 1.5s ease-in-out infinite;
}

@keyframes denied-pulse {
	0%, 100% { text-shadow: 0 0 8px #ff444460; }
	50% { text-shadow: 0 0 20px #ff444480, 0 0 40px #ff444440; }
}

a {
	text-decoration: none;
}
</style>
